import { Plus, EllipsisVertical, X } from "lucide-react";
import { some } from "lodash";
import React, { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { toast } from "@/design-system/components/Toast/Toast";

import { Button } from "@/design-system/components/Button/Button";
import { DropdownMenu } from "@/design-system/components/DropdownMenu/DropdownMenu";
import { StickyTableHead } from "@/design-system/components/Table/Table";
import {
  Tag,
  chipDismissButtonClassName,
} from "@/design-system/components/Tag/Tag";

import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import { makeRequest } from "../util";
import { AdvancedConfigProjectModal } from "./AdvancedConfigProjectModal";
import { EditProjectModal, ModalState } from "./EditProjectModal";
import {
  EditableEnvironment,
  EditableProject,
  EnvironmentsState,
} from "../reducers/environments";
import { fetchAccountMetadata } from "../actions/environments";
import {
  DeleteProjectModal,
  ProjectDeletionModalState,
} from "./DeleteProjectModal";
import {
  DeleteEnvironmentModal,
  EnvironmentDeletionModalState,
} from "./DeleteEnvironmentModal";

import useUsers from "../hooks/useUsers";
import FreshpaintUser from "../components/users/FreshpaintUser";

const EnvironmentChip: React.FC<{
  name: string;
  onDelete: () => void;
  showDelete: boolean;
}> = ({ name, onDelete, showDelete }) => {
  return (
    <Tag
      endContent={
        showDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className={chipDismissButtonClassName}
            aria-label="Delete environment"
          >
            <X className="size-3.5" strokeWidth={2.5} />
          </button>
        ) : undefined
      }
    >
      {name}
    </Tag>
  );
};

type ProjectAction = {
  label: string;
  handler: () => void;
};

const ProjectActionsMenu: React.FC<{
  project: EditableProject;
  showDelete: boolean;
  onEdit: () => void;
  onSettings: () => void;
  onDelete: () => void;
}> = ({ showDelete, onEdit, onSettings, onDelete }) => {
  const actions: ProjectAction[] = useMemo(() => {
    const items: ProjectAction[] = [
      { label: "Edit Project", handler: onEdit },
      { label: "Advanced Settings", handler: onSettings },
    ];
    if (showDelete) {
      items.push({ label: "Delete Project", handler: onDelete });
    }
    return items;
  }, [showDelete, onEdit, onSettings, onDelete]);

  return (
    <DropdownMenu
      options={actions}
      getOptionLabel={(a) => a.label}
      onSelectOption={(a) => a.handler()}
      icon={<EllipsisVertical className="h-5 w-5" />}
      triggerProps={{
        className: "h-8 w-8 rounded-md p-1.5 hover:bg-gray-100",
      }}
    />
  );
};

const ProjectsPage = () => {
  const dispatch = useDispatch();
  const accountStore: EnvironmentsState = useSelector((state) => {
    // @ts-ignore
    return state.environments;
  });
  const { accountMetadata, loaded } = accountStore;
  const { projects } = accountMetadata;
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] =
    React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [modalState, setModalState] = React.useState<ModalState | null>(null);
  const [projectDeletionModalOpen, setProjectDeletionModalOpen] =
    React.useState(false);
  const [environmentDeletionModalOpen, setEnvironmentDeletionModalOpen] =
    React.useState(false);
  const [settingsModalState, setSettingsModalState] =
    React.useState<ModalState | null>(null);
  const [projectDeletionModalState, setProjectDeletionModalState] =
    React.useState<ProjectDeletionModalState | null>(null);
  const [environmentDeletionModalState, setEnvironmentDeletionModalState] =
    React.useState<EnvironmentDeletionModalState | null>(null);

  const usersState = useUsers();
  const currentUser = usersState.loading
    ? null
    : new FreshpaintUser(usersState.currentUser);

  const loadAccountData = React.useCallback(
    (force = false) => {
      if (loaded && !force) {
        return;
      }
      dispatch(fetchAccountMetadata());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  const closeModal = () => {
    setErrorMessage("");
    setModalState(null);
    setSettingsModalState(null);
    setProjectDeletionModalState(null);
    setEnvironmentDeletionModalState(null);
    setModalOpen(false);
    setSettingsModalOpen(false);
    setProjectDeletionModalOpen(false);
    setEnvironmentDeletionModalOpen(false);
  };

  const saveProject = (errorMessage: string) => (project: EditableProject) => {
    if (!project.name) {
      setErrorMessage("You must set a project name.");
      return;
    }

    if (some(project.environments.map((e: EditableEnvironment) => !e.name))) {
      setErrorMessage("Every environment must have a name.");
      return;
    }

    setLoading(true);

    // Add a short delay so that the loading icon is clearly visible
    setTimeout(() => {
      makeRequest<{}>("save_project", project)
        .then((_response) => {
          setLoading(false);
          loadAccountData(true);
          closeModal();
          toast.success("Project Saved");
        })
        .catch((error) => {
          console.error("Error during saveProject: " + error);
          setLoading(false);
          setErrorMessage(errorMessage);
        });
    }, 500);
  };

  const deleteProject = (project: EditableProject) => {
    makeRequest<{}>("delete_project", project)
      .then(() => {
        loadAccountData(true);
        closeModal();
        toast.success("Project Deleted");
      })
      .catch(() => {
        toast.error(errorMessage);
      });
  };

  const deleteEnvironment = (environment: EditableEnvironment) => {
    makeRequest<{}>("delete_environment", environment)
      .then(() => {
        loadAccountData(true);
        closeModal();
        toast.success("Environment Deleted");
      })
      .catch(() => {
        toast.error(errorMessage);
      });
  };

  const saveNewProject = saveProject("Failed to create new project.");
  const updateProject = saveProject("Failed to update project.");

  const openCreateNewProjectModal = () => {
    const proj: EditableProject = {
      ID: null,
      accountID: "",
      name: "",
      environments: [
        {
          name: "Production",
          createdAt: null,
          ID: null,
          projectID: null,
          deletable: true,
          accountID: null,
        },
        {
          name: "Development",
          createdAt: null,
          ID: null,
          projectID: null,
          deletable: true,
          accountID: null,
        },
      ],
      config: {},
      eventPropertySensitivityLevels: {},
      userPropertySensitivityLevels: {},
      groupPropertySensitivityLevels: {},
    };
    setModalState({
      title: "New Project",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewProject: true,
      project: proj,
      onClose: closeModal,
      onSave: (newProjectState) => {
        saveNewProject(newProjectState);
      },
      isLoading: false,
    });
    setModalOpen(true);
  };

  const openUpdateProjectModal = (
    project: EditableProject,
    addNewEnv = false,
  ) => {
    setModalState({
      title: "Edit Project",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewEnvironment: addNewEnv,
      project,
      onClose: closeModal,
      onSave: (newProjectState) => {
        updateProject(newProjectState);
      },
      isLoading: false,
    });
    setModalOpen(true);
  };

  const openProjectSettingsModal = (
    project: EditableProject,
    addNewEnv = false,
  ) => {
    setSettingsModalState({
      title: "Advanced Settings",
      placeholder: "Project",
      closeOnOverlay: false,
      addingNewEnvironment: addNewEnv,
      project,
      onClose: closeModal,
      onSave: (newProjectState) => {
        updateProject(newProjectState);
      },
      isLoading: false,
    });
    setSettingsModalOpen(true);
  };

  const openProjectDeletionModal = (project: EditableProject) => {
    setProjectDeletionModalState({
      project,
      onClose: closeModal,
      onDelete: deleteProject,
    });
    setProjectDeletionModalOpen(true);
  };

  const openEnvironmentDeletionModal = (environment: EditableEnvironment) => {
    setEnvironmentDeletionModalState({
      environment,
      onClose: closeModal,
      onDelete: deleteEnvironment,
    });
    setEnvironmentDeletionModalOpen(true);
  };

  if (usersState.loading) {
    return <>Loading</>;
  }

  return (
    <>
      {modalOpen && modalState && (
        <EditProjectModal
          {...modalState}
          error={errorMessage}
          isLoading={isLoading}
        />
      )}
      {settingsModalOpen && settingsModalState && (
        <AdvancedConfigProjectModal
          {...settingsModalState}
          error={errorMessage}
        />
      )}
      {projectDeletionModalOpen && projectDeletionModalState && (
        <DeleteProjectModal {...projectDeletionModalState} />
      )}
      {environmentDeletionModalOpen && environmentDeletionModalState && (
        <DeleteEnvironmentModal {...environmentDeletionModalState} />
      )}
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Projects</h1>
          {currentUser?.hasAdminAccess() && (
            <Button onClick={() => openCreateNewProjectModal()}>
              <Plus className="mr-1.5 h-4 w-4" /> Add Project
            </Button>
          )}
        </div>
        <div className="border-surface-interactive overflow-hidden rounded-lg border bg-white shadow dark:bg-gray-100">
          <table className="w-full border-spacing-0 text-sm">
            <StickyTableHead>
              <tr>
                <th className="w-40 text-left font-normal">
                  <div className="flex items-center p-4 whitespace-nowrap">
                    Name
                  </div>
                </th>
                <th className="text-left font-normal">
                  <div className="flex items-center p-4 whitespace-nowrap">
                    Environments
                  </div>
                </th>
                <th className="w-32 text-center font-normal">
                  <div className="flex items-center justify-center p-4 whitespace-nowrap">
                    Actions
                  </div>
                </th>
              </tr>
            </StickyTableHead>
            <tbody className="divide-dividerDefault divide-y">
              {projects.map((project) => (
                <tr key={project.ID} className="group hover:bg-gray-50">
                  <td className="border-0 p-4">
                    <div className="text-textDefault truncate text-sm font-bold">
                      {project.name}
                    </div>
                  </td>
                  <td className="border-0 p-4">
                    {!!project.environments.length &&
                      project.environments.map((environment) => (
                        <EnvironmentChip
                          key={environment.ID}
                          name={environment.name}
                          onDelete={() =>
                            openEnvironmentDeletionModal(environment)
                          }
                          showDelete={!!currentUser?.hasAdminAccess()}
                        />
                      ))}
                    {!!project.environments.length &&
                      currentUser?.hasAdminAccess() && (
                        <Tag
                          endContent={<Plus className="size-4 shrink-0" />}
                          className="invisible group-hover:visible"
                          onClick={() => openUpdateProjectModal(project, true)}
                        >
                          Add Environment
                        </Tag>
                      )}
                    {!project.environments.length && (
                      <Tag
                        endContent={<Plus className="size-4 shrink-0" />}
                        onClick={() => openUpdateProjectModal(project, true)}
                      >
                        Add Environment
                      </Tag>
                    )}
                  </td>
                  <td className="border-0 p-4 text-center">
                    {currentUser?.hasAdminAccess() && (
                      <ProjectActionsMenu
                        project={project}
                        showDelete={projects.length > 1}
                        onEdit={() => openUpdateProjectModal(project)}
                        onSettings={() => openProjectSettingsModal(project)}
                        onDelete={() => openProjectDeletionModal(project)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

const breadcrumbs = [
  {
    label: "Settings",
  },
  {
    label: "Projects",
    href: "/settings/projects",
  },
];

function ProjectsPageWithNavigation() {
  useSetBreadcrumbs(breadcrumbs);

  return <ProjectsPage />;
}

export default ProjectsPageWithNavigation;
